import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const ProofOfDeliveryUploader = ({ orderId, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Show preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setUploadError(null);
    setUploadSuccess(false);

    await uploadProof(file);
  };

  const uploadProof = async (file) => {
    if (!orderId) {
      setUploadError('No order ID — cannot upload proof.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const ext = file?.name?.split('.')?.pop() || 'jpg';
      const filePath = `${orderId}/proof_${Date.now()}.${ext}`;

      const { error: uploadErr } = await supabase?.storage
        ?.from('delivery-proofs')
        ?.upload(filePath, file, { upsert: true, contentType: file?.type });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase?.storage
        ?.from('delivery-proofs')
        ?.getPublicUrl(filePath);

      const proofUrl = urlData?.publicUrl;

      // Store URL in orders.delivery_details.proof_of_delivery_url
      const { data: orderData, error: fetchErr } = await supabase
        ?.from('orders')
        ?.select('delivery_details')
        ?.eq('id', orderId)
        ?.single();

      if (fetchErr) throw fetchErr;

      const updatedDetails = {
        ...(orderData?.delivery_details || {}),
        proof_of_delivery_url: proofUrl,
        proof_uploaded_at: new Date()?.toISOString(),
      };

      const { error: updateErr } = await supabase
        ?.from('orders')
        ?.update({ delivery_details: updatedDetails })
        ?.eq('id', orderId);

      if (updateErr) throw updateErr;

      setUploadSuccess(true);
      onUploadComplete?.(proofUrl);
    } catch (err) {
      console.error('Proof upload failed:', err);
      setUploadError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const triggerCamera = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
      fileInputRef?.current?.click();
    }
  };

  return (
    <div className="rounded-2xl bg-[#0f2040] border border-[#ffd700]/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">📸</span>
        <h3 className="text-white font-bold text-sm">Proof of Delivery</h3>
        {uploadSuccess && (
          <span className="ml-auto text-xs font-bold text-green-400 flex items-center gap-1">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
            Uploaded
          </span>
        )}
      </div>

      {/* Hidden file input — capture=environment opens rear camera on mobile */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={handleFileSelect}
      />

      {previewUrl ? (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border border-white/10">
            <img
              src={previewUrl}
              alt="Proof of delivery photo"
              className="w-full h-40 object-cover"
            />
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-[#ffd700] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-white text-xs font-semibold">Uploading...</p>
                </div>
              </div>
            )}
            {uploadSuccess && (
              <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
                <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              </div>
            )}
          </div>
          {uploadError && (
            <p className="text-red-400 text-xs font-medium">{uploadError}</p>
          )}
          {!uploadSuccess && !uploading && (
            <button
              onClick={triggerCamera}
              className="w-full py-2.5 rounded-xl bg-white/10 text-white/70 font-semibold text-xs hover:bg-white/15 transition-all active:scale-95"
            >
              Retake Photo
            </button>
          )}
        </div>
      ) : (
        <div>
          <p className="text-white/40 text-xs mb-3">
            Take a photo of the delivered package at the recipient's door.
          </p>
          <button
            onClick={triggerCamera}
            disabled={uploading}
            className="w-full py-3.5 rounded-xl bg-[#ffd700] text-[#0a1628] font-black text-sm flex items-center justify-center gap-2 hover:bg-[#f0c800] transition-all active:scale-95 disabled:opacity-60"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8 3.2 3.2 0 0 1 15.2 12 3.2 3.2 0 0 1 12 15.2M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9m3 15a5 5 0 0 1-5-5 5 5 0 0 1 5-5 5 5 0 0 1 5 5 5 5 0 0 1-5 5z" />
            </svg>
            Take Proof Photo
          </button>
          {uploadError && (
            <p className="text-red-400 text-xs font-medium mt-2">{uploadError}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProofOfDeliveryUploader;
